/**
 * Shell de la aplicación — responsivo de verdad.
 *
 *  · ≥1024px: sidebar fija a la izquierda (usa toda la altura, sin scroll horizontal).
 *  · <1024px: barra inferior tipo app (tabs), con "más" si hay más de 5 destinos.
 *  · La navegación se DERIVA de la matriz de permisos (SSOT S4): no hay pestañas
 *    escondidas con CSS, y quien no tiene permiso ni ve la ruta ni descarga el chunk.
 */
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { can, type Action, type Resource, type Role } from '@thepit/contracts';
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth.js';
import { useT } from '../lib/i18n.js';
import { OutboxBadge } from './OutboxBadge.js';
import { Button } from './ui.js';
import { applyTheme, resolveInitialTheme, type ThemeName } from '@thepit/tokens';
import { getSetting, setSetting } from '../lib/db.js';
import { getSoundEnabled, toggleSound, subscribeSound } from '../lib/audio.js';

interface NavItem {
  key: string;
  /** Etiqueta corta para la barra inferior móvil (si falta, usa `key`). */
  short?: string;
  path: string;
  resource: Resource;
  action: Action;
  /** Roles extra permitidos además del permiso (p. ej. HUD es de admin y viewer). */
  roles?: readonly Role[];
  /** Icono en texto: se dibuja con una sola letra, sin dependencia de fuentes. */
  glyph: string;
}

const NAV: readonly NavItem[] = [
  { key: 'nav.pelea', short: 'nav.peleaShort', path: '/pelea', resource: 'fight', action: 'read', glyph: '⚐' },
  { key: 'nav.misApuestas', short: 'nav.misApuestasShort', path: '/mis-apuestas', resource: 'bet', action: 'read_own', glyph: '◈' },
  { key: 'nav.mesa', short: 'nav.mesaShort', path: '/mesa', resource: 'bet', action: 'create', glyph: '▸' },
  { key: 'nav.caja', path: '/caja', resource: 'payout', action: 'pay', glyph: '≡' },
  { key: 'nav.hud', path: '/hud', resource: 'fight', action: 'read', roles: ['admin', 'viewer'], glyph: '▣' },
  { key: 'nav.historial', path: '/historial', resource: 'history', action: 'read', glyph: '◷' },
  { key: 'nav.ajustes', path: '/ajustes', resource: 'settings', action: 'update', glyph: '⚙' },
  { key: 'nav.presets', path: '/presets', resource: 'preset', action: 'read', glyph: '❦' },
  { key: 'nav.usuarios', path: '/usuarios', resource: 'user', action: 'read', glyph: '◐' },
  { key: 'nav.auditoria', path: '/auditoria', resource: 'audit', action: 'read', glyph: '▤' },
];

export function visibleNav(role: Role): NavItem[] {
  return NAV.filter(
    (item) => can(role, item.resource, item.action) && (!item.roles || item.roles.includes(role)),
  );
}

/**
 * Prefetch de los chunks de ruta que el rol SÍ puede ver. Respeta §6 (nunca se
 * descarga el chunk de una ruta prohibida) y hace que la navegación sea instantánea:
 * al entrar a una vista el módulo ya está en caché, sin flash de skeleton.
 */
const ROUTE_CHUNKS: Record<string, () => Promise<unknown>> = {
  '/mesa': () => import('../routes/Mesa.js'),
  '/caja': () => import('../routes/Caja.js'),
  '/historial': () => import('../routes/Historial.js'),
  '/ajustes': () => import('../routes/Ajustes.js'),
  '/presets': () => import('../routes/Presets.js'),
  '/usuarios': () => import('../routes/Usuarios.js'),
  '/auditoria': () => import('../routes/Auditoria.js'),
};

/** En móvil solo caben 4 tabs + botón "más". */
const MOBILE_LIMIT = 4;

/** Iniciales para el avatar de usuario. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Logo hexagonal THE PIT (marca de la app). */
function BrandMark({ size = 32 }: { size?: number }): JSX.Element {
  return (
    <span
      aria-hidden
      className="pit-hex grid shrink-0 place-items-center"
      style={{
        height: size,
        width: size,
        background:
          'linear-gradient(160deg, color-mix(in srgb, var(--pit-accent-red) 68%, transparent), color-mix(in srgb, var(--pit-accent-amber) 26%, transparent))',
        boxShadow: '0 0 18px -6px var(--pit-accent-red)',
      }}
    >
      <span
        className="pit-mono font-[var(--pit-weight-black)] text-white"
        style={{ fontSize: size * 0.34, letterSpacing: '0.02em' }}
      >
        TP
      </span>
    </span>
  );
}

export function Layout(): JSX.Element {
  const { me, logout } = useAuth();
  const t = useT();
  const location = useLocation();
  const [theme, setTheme] = useState<ThemeName>('dark');
  const [soundOn, setSoundOn] = useState(getSoundEnabled());
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    return subscribeSound(setSoundOn);
  }, []);

  useEffect(() => {
    void (async () => {
      const stored = await getSetting('theme');
      const resolved = resolveInitialTheme(stored);
      setTheme(resolved);
      applyTheme(resolved);
    })();
  }, []);

  // Cierra el menú "más" al navegar.
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  const toggleTheme = async (): Promise<void> => {
    const next: ThemeName = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
    await setSetting('theme', next);
  };

  const role = me?.user.role ?? 'viewer';
  const items = visibleNav(role);
  const mobileMain = items.slice(0, MOBILE_LIMIT);
  const mobileRest = items.slice(MOBILE_LIMIT);

  // Precarga en idle los chunks de las rutas visibles para este rol.
  const navPaths = items.map((i) => i.path).join(',');
  useEffect(() => {
    if (!navPaths) return undefined;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const schedule = w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 150));
    const cancel = w.cancelIdleCallback ?? ((id: number) => window.clearTimeout(id));
    const id = schedule(() => {
      for (const path of navPaths.split(',')) void ROUTE_CHUNKS[path]?.();
    });
    return () => cancel(id);
  }, [navPaths]);

  const sidebarLink = (isActive: boolean): string =>
    [
      'pit-nav-item relative flex items-center gap-3 rounded-[var(--pit-radius-md)] px-3 py-2',
      isActive ? 'pit-nav-active' : 'pit-nav-idle',
    ].join(' ');

  const tabLink = (isActive: boolean): string =>
    [
      'pit-tab pit-touch flex w-full min-w-[4.5rem] flex-col items-center gap-1 px-1 py-1.5',
      isActive ? 'pit-tab-active' : 'pit-tab-idle',
    ].join(' ');

  const glyphClass = (base: string): string => `${base} pit-nav-glyph`;

  const soundLabel = t(soundOn ? 'app.soundOn' : 'app.soundOff');

  return (
    <div className="pit-scanlines relative flex min-h-full flex-col bg-[var(--pit-bg-base)] lg:flex-row">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[var(--pit-z-toast)] focus:rounded focus:bg-[var(--pit-bg-overlay)] focus:px-3 focus:py-2"
      >
        {t('a11y.skipToContent')}
      </a>

      {/* Sidebar (escritorio) */}
      <aside className="pit-sidebar sticky top-0 hidden h-screen w-60 shrink-0 flex-col lg:flex">
        <div className="border-b border-[var(--pit-border-subtle)] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <BrandMark size={34} />
            <div className="min-w-0">
              <span className="pit-title pit-mono block text-[var(--pit-text-base)] font-[var(--pit-weight-black)] uppercase tracking-[var(--pit-tracking-widest)]">
                {t('app.name')}
              </span>
              <span className="block truncate text-[var(--pit-text-3xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-muted)]">
                {t('app.tagline')}
              </span>
            </div>
          </div>
        </div>

        <nav aria-label={t('app.name')} className="flex-1 overflow-y-auto px-2 py-2">
          <ul className="flex flex-col gap-0.5">
            {items.map((item) => (
              <li key={item.path}>
                <NavLink to={item.path} className={({ isActive }) => sidebarLink(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden
                        className={[
                          'absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-full bg-[var(--pit-accent-red)] shadow-[0_0_8px_var(--pit-accent-red)]',
                          'transition-opacity duration-[var(--pit-duration-fast)]',
                          isActive ? 'opacity-100' : 'opacity-0',
                        ].join(' ')}
                      />
                      <span aria-hidden className={glyphClass('pit-mono w-5 text-center text-[var(--pit-text-sm)]')}>
                        {item.glyph}
                      </span>
                      <span className="pit-mono text-[var(--pit-text-xs)] uppercase tracking-[var(--pit-tracking-wider)]">
                        {t(item.key)}
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-[var(--pit-border-subtle)] px-3 py-2.5">
          {me && (
            <div className="mb-2 flex items-center gap-2.5">
              <span aria-hidden className="pit-avatar h-8 w-8 shrink-0">
                <span className="pit-mono text-[var(--pit-text-2xs)] font-[var(--pit-weight-black)]">
                  {initials(me.user.displayName)}
                </span>
              </span>
              <div className="min-w-0">
                <p className="truncate text-[var(--pit-text-sm)] font-[var(--pit-weight-semibold)]">
                  {me.user.displayName}
                </p>
                <p className="pit-mono truncate text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-muted)]">
                  {t(`users.roles.${me.user.role}`)} · {me.org.name}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Button variant="ghost" onClick={() => toggleSound()} aria-label={soundLabel} title={soundLabel}>
              {soundOn ? '♪' : '—'}
            </Button>
            <Button variant="ghost" onClick={() => void toggleTheme()} aria-label={t('nav.tema')}>
              {theme === 'dark' ? '☾' : '☼'}
            </Button>
            <Button variant="ghost" className="ml-auto" onClick={() => void logout()}>
              {t('nav.salir')}
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar (móvil y tablet) */}
        <header className="pit-topbar sticky top-0 z-[var(--pit-z-sticky)] lg:hidden">
          <div className="flex items-center gap-2 px-3 py-2">
            <BrandMark size={28} />
            <span className="pit-title pit-mono text-[var(--pit-text-base)] font-[var(--pit-weight-black)] uppercase tracking-[var(--pit-tracking-widest)]">
              {t('app.name')}
            </span>
            <div className="ml-auto flex items-center gap-1">
              <OutboxBadge />
              <Button variant="ghost" onClick={() => toggleSound()} aria-label={soundLabel}>
                {soundOn ? '♪' : '—'}
              </Button>
              <Button variant="ghost" onClick={() => void toggleTheme()} aria-label={t('nav.tema')}>
                {theme === 'dark' ? '☾' : '☼'}
              </Button>
              <Button variant="ghost" onClick={() => void logout()}>
                {t('nav.salir')}
              </Button>
            </div>
          </div>
        </header>

        <main id="contenido" className="mx-auto w-full max-w-[125rem] flex-1 px-3 pb-24 pt-4 lg:px-6 lg:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Barra inferior (móvil) */}
      <nav aria-label={t('app.name')} className="pit-bottombar fixed inset-x-0 bottom-0 z-[var(--pit-z-sticky)] lg:hidden">
        <ul className="flex items-stretch">
          {mobileMain.map((item) => (
            <li key={item.path} className="flex-1">
              <NavLink to={item.path} className={({ isActive }) => tabLink(isActive)}>
                {() => (
                  <>
                    <span aria-hidden className={glyphClass('pit-mono text-[var(--pit-text-base)]')}>
                      {item.glyph}
                    </span>
                    <span className="pit-mono text-[var(--pit-text-3xs)] uppercase tracking-[var(--pit-tracking-wider)]">
                      {t(item.short ?? item.key)}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
          {mobileRest.length > 0 && (
            <li className="flex-1">
              <button
                type="button"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((v) => !v)}
                className={[
                  'pit-tab pit-touch flex w-full flex-col items-center gap-1 px-2 py-1.5',
                  moreOpen ? 'pit-tab-active' : 'pit-tab-idle',
                ].join(' ')}
              >
                <span aria-hidden className={glyphClass('pit-mono text-[var(--pit-text-base)]')}>
                  ⋯
                </span>
                <span className="pit-mono text-[var(--pit-text-3xs)] uppercase tracking-[var(--pit-tracking-wider)]">
                  {t('nav.more')}
                </span>
              </button>
            </li>
          )}
        </ul>

        {moreOpen && (
          <div className="pit-anim-up border-t border-[var(--pit-border-subtle)] bg-[var(--pit-bg-overlay)] p-2">
            <ul className="grid grid-cols-2 gap-1">
              {mobileRest.map((item) => (
                <li key={item.path}>
                  <NavLink to={item.path} className={({ isActive }) => sidebarLink(isActive)}>
                    <span aria-hidden className={glyphClass('pit-mono w-5 text-center text-[var(--pit-text-sm)]')}>
                      {item.glyph}
                    </span>
                    <span className="pit-mono text-[var(--pit-text-xs)] uppercase tracking-[var(--pit-tracking-wider)]">
                      {t(item.key)}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>
    </div>
  );
}
